import React, { useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Loader2, Settings2, AlertTriangle } from 'lucide-react';
import { DAYS } from './NoteGrid';

const Row = ({ label, help, children, testid }) => (
  <div className="flex items-center justify-between gap-3 py-2 border-b border-border/50 last:border-b-0" data-testid={testid}>
    <div className="min-w-0">
      <p className="text-xs text-foreground">{label}</p>
      {help && <p className="text-[10px] text-muted-foreground">{help}</p>}
    </div>
    {children}
  </div>
);

export const GlobalSettingsCard = ({ settings, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [office, setOffice] = useState(null);
  const [defMin, setDefMin] = useState(null);
  const [confirmLive, setConfirmLive] = useState(false);
  if (!settings) return null;
  const oh = office || settings.office_hours;
  const defaultMinutes = defMin ?? Math.round((settings.default_display_seconds || 300) / 60);

  const save = async (patch, okMsg) => {
    setBusy(true);
    try { await api.vbManagerSaveSettings(patch); if (okMsg) toast.success(okMsg); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setBusy(false);
  };
  const toggleDay = (d) => {
    const days = oh.days.includes(d) ? oh.days.filter(x => x !== d) : [...oh.days, d].sort();
    setOffice({ ...oh, days });
  };

  return (
    <div className="bg-card border border-border rounded-sm p-5" data-testid="vb-global-settings">
      <p className="text-sm font-medium text-foreground flex items-center gap-2 mb-2"><Settings2 className="w-4 h-4 text-muted-foreground" /> Globale Einstellungen</p>
      <Row label={settings.dry_run ? 'DRY_RUN — es wird nichts gesendet' : 'LIVE — sendet an die Note'} help="Frames landen im Log statt auf dem Display" testid="vb-row-dry-run">
        {settings.dry_run && !confirmLive ? (
          <Button size="sm" onClick={() => setConfirmLive(true)} className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="vb-go-live-btn">Live schalten</Button>
        ) : settings.dry_run && confirmLive ? (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-amber-300 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Wirklich echt senden?</span>
            <Button size="sm" disabled={busy} onClick={() => { setConfirmLive(false); save({ dry_run: false }, 'Board Manager ist LIVE'); }} className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="vb-go-live-confirm-btn">Ja, live</Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmLive(false)} className="h-7 text-[11px]" data-testid="vb-go-live-cancel-btn">Abbrechen</Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => save({ dry_run: true }, 'Zurück im DRY_RUN')} className="h-7 text-[11px]" data-testid="vb-dry-run-btn">Zurück zu DRY_RUN</Button>
        )}
      </Row>
      <Row label="Pause" help="friert die aktuelle Nachricht ein" testid="vb-row-paused">
        <Switch checked={!!settings.paused} disabled={busy} onCheckedChange={(v) => save({ paused: v }, v ? 'Pausiert' : 'Pause beendet')} data-testid="vb-paused-switch" />
      </Row>
      <Row label="Besucher-Modus" help="pausiert alle Boards mit Deal-Daten; ihre Events warten" testid="vb-row-visitor">
        <Switch checked={!!settings.visitor_mode} disabled={busy} onCheckedChange={(v) => save({ visitor_mode: v }, v ? 'Besucher-Modus an' : 'Besucher-Modus aus')} data-testid="vb-visitor-switch" />
      </Row>
      <div className="py-2 border-b border-border/50">
        <p className="text-xs text-foreground">Bürozeiten <span className="text-[10px] text-muted-foreground">({oh.tz})</span></p>
        <p className="text-[10px] text-muted-foreground mb-1.5">außerhalb wird nichts gesendet, Events warten</p>
        <div className="flex items-center gap-1 flex-wrap">
          {DAYS.map((d, i) => (
            <button key={d} onClick={() => toggleDay(i)} data-testid={`vb-office-day-${i}`}
              className={`text-[10px] px-2 py-1 rounded-sm border ${oh.days.includes(i) ? 'bg-amber-500/15 text-amber-300 border-amber-500/40' : 'text-muted-foreground border-border'}`}>{d}</button>
          ))}
          <input type="time" value={oh.start} onChange={e => setOffice({ ...oh, start: e.target.value })} data-testid="vb-office-start"
            className="ml-2 bg-secondary border border-border rounded-sm px-1.5 py-0.5 text-xs font-mono text-foreground" />
          <span className="text-[10px] text-muted-foreground">–</span>
          <input type="time" value={oh.end} onChange={e => setOffice({ ...oh, end: e.target.value })} data-testid="vb-office-end"
            className="bg-secondary border border-border rounded-sm px-1.5 py-0.5 text-xs font-mono text-foreground" />
          {office && (
            <Button size="sm" disabled={busy} onClick={() => { save({ office_hours: office }, 'Bürozeiten gespeichert'); setOffice(null); }} className="h-6 text-[10px] ml-1" data-testid="vb-office-save-btn">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Speichern'}
            </Button>
          )}
        </div>
      </div>
      <Row label="Standard-Anzeigezeit neuer Rotation-Boards" testid="vb-row-default-display">
        <div className="flex items-center gap-1.5">
          <input type="number" min="1" max="1440" value={defaultMinutes} onChange={e => setDefMin(e.target.value)} data-testid="vb-default-display-input"
            className="w-16 bg-secondary border border-border rounded-sm px-1.5 py-0.5 text-xs font-mono text-foreground text-right" />
          <span className="text-[10px] text-muted-foreground">Min</span>
          {defMin !== null && Number(defMin) !== Math.round((settings.default_display_seconds || 300) / 60) && (
            <Button size="sm" disabled={busy} onClick={() => { save({ default_display_seconds: Math.max(1, Number(defMin)) * 60 }, 'Gespeichert'); setDefMin(null); }} className="h-6 text-[10px]" data-testid="vb-default-display-save-btn">OK</Button>
          )}
        </div>
      </Row>
      <p className="text-[10px] text-muted-foreground mt-2">Höchstens ein Update alle {settings.min_gap_seconds} s · Go-Live der Zählung: {settings.go_live_at ? new Date(settings.go_live_at).toLocaleDateString('de-DE') : '—'}</p>
    </div>
  );
};
